import React, { useState, useEffect } from 'react';
import Autocomplete from './Autocomplete';
import stations from '../data/stations.json';
import { OutboundPickers } from './OutboundPickers';
import { NextPlan } from './NextPlan';
import { JourneySummary } from './JourneySummary';
import { ShareScreen } from './ShareScreen';
import { getLocationByName, updateFavourite } from '../config/devices';
// PageLayout is now handled by parent App.js
import moment from 'moment';
import { BeatLoader } from 'react-spinners';
import { sendAnalyticsEvent } from '../services/analyticsService';

function JourneyApp({ onDisconnect, onFullscreen, isFullscreen, isStandalone = false, selectedLocation, favouriteClickData, onFavouriteProcessed, onFavouriteUpdated }) {
  const [date, setDate] = useState(moment());
  const [time, setTime] = useState(moment());
  const [selectedRoute, setSelectedRoute] = useState({
    destination: {
      name: '',
      stopCode: '',
      stopCodeType: ''
    }
  });

  // Get origin details from selected location
  const getOriginDetails = (mode = 'RAIL') => {
    if (!selectedLocation) {
      // Fallback to Bristol Temple Meads if no location selected
      return {
        name: "Bristol Temple Meads",
        stopCode: mode === 'BUS' ? "gcnhtq07" : "BRI",
        stopCodeType: mode === 'BUS' ? "GEOHASH" : "CRS"
      };
    }
    
    // Look up location in master LOCATIONS list
    const locationData = getLocationByName(selectedLocation);
    
    if (!locationData) {
      // Fallback if location not found in master list
      return {
        name: selectedLocation,
        stopCode: "UNK",
        stopCodeType: mode === 'BUS' ? "GEOHASH" : "CRS"
      };
    }
    
    // For bus mode, use GEOHASH if available, otherwise fall back to CRS
    if (mode === 'BUS') {
      return {
        name: locationData.name,
        stopCode: locationData.geohash || locationData.crs,
        stopCodeType: locationData.geohash ? "GEOHASH" : "CRS"
      };
    }
    
    // For rail mode, use CRS
    return {
      name: locationData.name,
      stopCode: locationData.crs,
      stopCodeType: "CRS"
    };
  };

  const [nextPlans, setNextPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(null);
  const [showShareScreen, setShowShareScreen] = useState(false);
  const [selectedMode, setSelectedMode] = useState('RAIL');
  
  // Calculate origin details based on current selected mode
  const originDetails = getOriginDetails(selectedMode);
  const [showStations, setShowStations] = useState(true);
  const [showNoResultsModal, setShowNoResultsModal] = useState(false);
  const [showGooglePlaces, setShowGooglePlaces] = useState(false);

  // Handle favourite clicks from PageLayout
  useEffect(() => {
    if (favouriteClickData) {
      // Reset to search screen state first
      setNextPlans([]);
      setSelectedPlanIndex(null);
      setShowShareScreen(false);
      setShowNoResultsModal(false);
      
      // Set the correct mode based on the favourite type
      // If stopCodeType is 'CRS', it's a rail station, otherwise it's a bus location
      const isRailFavourite = favouriteClickData.stopCodeType === 'CRS';
      setSelectedMode(isRailFavourite ? 'RAIL' : 'BUS');
      
      // Then set the destination
      setSelectedRoute(prev => ({
        ...prev,
        destination: {
          name: favouriteClickData.name,
          stopCode: favouriteClickData.stopCode,
          stopCodeType: favouriteClickData.stopCodeType
        }
      }));
      
      // Clear the favourite click data
      if (onFavouriteProcessed) {
        onFavouriteProcessed();
      }
    }
  }, [favouriteClickData, onFavouriteProcessed]);

  // Connection loss is now handled by parent App component

  // Handle mode changes
  useEffect(() => {
    if (selectedMode === 'RAIL') {
      setShowStations(true);
      setShowGooglePlaces(false);
    } else {
      setShowStations(false);
      setShowGooglePlaces(true);
    }
  }, [selectedMode]);

  // Handle disconnect and return to connection screen
  const handleDisconnect = async () => {
    if (onDisconnect) {
      await onDisconnect();
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    const datetime = date?.set('hour', time?.hour() || 0).set('minute', time?.minute() || 0);

    const query = `
      query GetLiveData($journeyPlanEvents: [String!]!) {
        getLiveData(journeyPlanEvents: $journeyPlanEvents)
      }
    `;

    try {
      const response = await fetch('https://k3oabpeoenb7ha3xvswrhugwy4.appsync-api.eu-west-2.amazonaws.com/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'da2-macnjdldqzc3vfizuivi7cpjjm'
        },
        body: JSON.stringify({
          query,
          variables: {
            journeyPlanEvents: [
              JSON.stringify({
                origin: {
                  name: originDetails.name,
                  stopCode: originDetails.stopCode,
                  stopCodeType: originDetails.stopCodeType
                },
                destination: {
                  name: selectedRoute.destination.name,
                  stopCode: selectedRoute.destination.stopCode,
                  stopCodeType: selectedRoute.destination.stopCodeType
                },
                region: "GB",
                datetime: datetime.format('YYYY-MM-DDTHH:mm:ssZ'),
                numItineraries: 3,
                arriveBy: false,
                features: selectedMode === 'BUS' ? 'BUSONLY' : 'RAILONLY'
              })
            ]
          }
        })
      });

      const data = await response.json();
      const liveDataString = data.data.getLiveData;

      // Parse the JSON string to get the actual array
      const liveData = JSON.parse(liveDataString);

      // Convert the record to an array of JourneyPlanWithNode
      const plansArray = liveData
        .filter(plan => plan !== null && plan !== undefined)
        .filter(plan => plan.node?.legs?.some(leg => leg.trip?.gtfsId));
      
      setNextPlans(plansArray);
      
      // Send analytics for search
      await sendAnalyticsEvent('device_ticket-window_search', {
        location: selectedLocation,
        origin: originDetails.name,
        originCode: originDetails.stopCode,
        destination: selectedRoute.destination.name,
        destinationCode: selectedRoute.destination.stopCode,
        mode: selectedMode,
        datetime: datetime.format('YYYY-MM-DDTHH:mm:ssZ'),
        resultsCount: plansArray.length,
        isStandalone: isStandalone
      });
      
      // Track the search in favourites if we have results
      if (plansArray.length > 0 && selectedLocation && selectedRoute.destination.name) {
        updateFavourite(selectedLocation, selectedRoute.destination, selectedMode);
        // Trigger favourites refresh in PageLayout
        if (onFavouriteUpdated) {
          onFavouriteUpdated();
        }
      }
      
      // Show no results modal if search returned empty results
      if (plansArray.length === 0) {
        setShowNoResultsModal(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Also show error modal for API errors
      setShowNoResultsModal(true);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-8">
            <BeatLoader color="#D7C20B" size={15} />
          </div>
        </div>
      )}
      
      {/* Main Journey App Content - PageLayout wrapper is handled by App.js */}
      <div className="h-full flex flex-col relative overflow-hidden">
        {/* Header */}
        <div className="pt-4 sm:pt-8 px-4 sm:px-8 flex justify-between items-center mb-4 sm:mb-8 flex-shrink-0">
          <div className="flex-1 pr-2">
            <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-black">
              Journeys from {originDetails.name} to:
            </h1>
          </div>
          <button
            onClick={handleDisconnect}
            className={`w-10 h-10 sm:w-12 sm:h-12 ${isStandalone ? 'text-gray-400' : 'text-green-500'} rounded-full flex items-center justify-center transition-colors`}
            title={isStandalone ? "Return to device selection" : "Disconnect and return to device selection"}
          >
            <i className="zip-icon-feat_connected text-2xl sm:text-3xl lg:text-4xl"></i>
          </button>
        </div>

        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto">
          {/* Main Content - Responsive Layout */}
          <div className="px-4 sm:px-8 pb-24">
            {/* Desktop/Landscape Layout */}
            <div className="hidden landscape:flex flex-col h-full gap-6">
              {/* Top section with mode selector and destination */}
              <div className="flex items-center mt-8">
                {/* Mode Selection - Fixed width to align with date buttons */}
                <div className="flex gap-4 w-56 ml-2">
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => {
                        setSelectedMode('RAIL');
                        setSelectedRoute(prev => ({
                          ...prev,
                          destination: { name: '', stopCode: '', stopCodeType: '' }
                        }));
                      }}
                      className={`w-16 h-16 flex items-center justify-center border-2 border-journeyAlertsPurple rounded-full cursor-pointer transition-colors ${
                        selectedMode === 'RAIL' ? 'bg-journeyAlertsPurple text-white' : 'bg-white text-journeyAlertsPurple'
                      }`}
                    >
                      <i className="text-3xl zip-icon-mode_rail flex items-center justify-center leading-none"></i>
                    </button>
                    <span className="text-lg mt-2 text-black font-medium">Rail</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => {
                        setSelectedMode('BUS');
                        setSelectedRoute(prev => ({
                          ...prev,
                          destination: { name: '', stopCode: '', stopCodeType: '' }
                        }));
                      }}
                      className={`w-16 h-16 flex items-center justify-center border-2 border-journeyAlertsPurple rounded-full cursor-pointer transition-colors ${
                        selectedMode === 'BUS' ? 'bg-journeyAlertsPurple text-white' : 'bg-white text-journeyAlertsPurple'
                      }`}
                    >
                      <i className="text-3xl zip-icon-mode_bus flex items-center justify-center leading-none"></i>
                    </button>
                    <span className="text-lg mt-2 text-black font-medium">Bus</span>
                  </div>
                </div>

                {/* Destination Input - Fixed width to align with date/time picker */}
                <div className="w-[24rem] ml-2">
                  <div className="h-20 flex items-center py-4 px-6 bg-journeyAlertsGrey rounded-2xl">
                    <div className="w-full">
                      <Autocomplete
                        placeholder="look up destination"
                        locations={stations}
                        showStations={showStations}
                        showGooglePlaces={showGooglePlaces}
                        value={selectedRoute.destination?.name ?? ''}
                        onSelect={(location) => {
                          setSelectedRoute(prev => ({
                            ...prev,
                            destination: {
                              name: location.name,
                              stopCode: location.stopCode,
                              stopCodeType: location.stopCodeType
                            }
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date/Time section */}
              <div className="flex items-center">
                {/* Date Selection Buttons - Same width as mode selection */}
                <div className="flex gap-4 w-56 ml-2">
                  <button
                    onClick={() => {
                      const now = moment();
                      setDate(now);
                      setTime(now);
                    }}
                    style={{ lineHeight: 'normal' }}
                    className={`text-lg px-4 py-3 h-14 rounded-full transition-colors font-medium ${date.isSame(moment(), 'day')
                      ? 'bg-journeyAlertsPurple text-white'
                      : 'bg-white text-black border-2 border-journeyAlertsPurple'
                      }`}
                  >
                    Now
                  </button>
                  <button
                    onClick={() => setDate(moment().add(1, 'day'))}
                    style={{ lineHeight: 'normal' }}
                    className={`text-lg px-4 py-3 h-14 rounded-full transition-colors font-medium ${date.isSame(moment().add(1, 'day'), 'day')
                      ? 'bg-journeyAlertsPurple text-white'
                      : 'bg-white text-black border-2 border-journeyAlertsPurple'
                      }`}
                  >
                    Tomorrow
                  </button>
                </div>

                {/* Date and Time Input - Same width and position as destination */}
                <div className="w-[24rem] ml-2">
                  <div className="h-20 flex items-center py-4 px-6 bg-journeyAlertsGrey rounded-2xl">
                    <div className="bg-white rounded-md p-1 w-full">
                      <OutboundPickers
                        setDate={setDate}
                        date={date}
                        setTime={setTime}
                        time={time}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Portrait/Mobile Layout */}
            <div className="flex flex-col landscape:hidden">
              {/* Mode Selection Buttons - Mobile */}
              <div className="flex gap-2 mb-4 justify-center">
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => {
                      setSelectedMode('RAIL');
                      setSelectedRoute(prev => ({
                        ...prev,
                        destination: { name: '', stopCode: '', stopCodeType: '' }
                      }));
                    }}
                    className={`w-16 h-16 flex items-center justify-center border-2 border-journeyAlertsPurple rounded-full cursor-pointer transition-colors ${
                      selectedMode === 'RAIL' ? 'bg-journeyAlertsPurple text-white' : 'bg-white text-journeyAlertsPurple'
                    }`}
                  >
                    <i className="text-2xl zip-icon-mode_rail flex items-center justify-center leading-none"></i>
                  </button>
                  <span className="text-base mt-2 capitalize font-medium">Train</span>
                </div>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => {
                      setSelectedMode('BUS');
                      setSelectedRoute(prev => ({
                        ...prev,
                        destination: { name: '', stopCode: '', stopCodeType: '' }
                      }));
                    }}
                    className={`w-16 h-16 flex items-center justify-center border-2 border-journeyAlertsPurple rounded-full cursor-pointer transition-colors ${
                      selectedMode === 'BUS' ? 'bg-journeyAlertsPurple text-white' : 'bg-white text-journeyAlertsPurple'
                    }`}
                  >
                    <i className="text-2xl zip-icon-mode_bus flex items-center justify-center leading-none"></i>
                  </button>
                  <span className="text-base mt-2 capitalize font-medium">Bus/Tram</span>
                </div>
              </div>

              {/* Destination Input */}
              <div className="flex flex-col gap-2 py-4 px-4 bg-journeyAlertsGrey rounded-2xl relative mb-4">
                <Autocomplete
                  placeholder="look up destination"
                  locations={stations}
                  showStations={showStations}
                  showGooglePlaces={showGooglePlaces}
                  value={selectedRoute.destination?.name ?? ''}
                  onSelect={(location) => {
                    setSelectedRoute(prev => ({
                      ...prev,
                      destination: {
                        name: location.name,
                        stopCode: location.stopCode,
                        stopCodeType: location.stopCodeType
                      }
                    }));
                  }}
                />
              </div>

              {/* Date and Time Input */}
              <div className="flex flex-col gap-2 py-4 px-4 bg-journeyAlertsGrey rounded-2xl relative mb-4">
                <div className="bg-white rounded-md p-2">
                  <OutboundPickers
                    setDate={setDate}
                    date={date}
                    setTime={setTime}
                    time={time}
                  />
                </div>
              </div>

              {/* Date Selection Buttons - Below inputs in portrait */}
              <div className="flex gap-2 justify-center mb-4">
                <button
                  onClick={() => {
                    const now = moment();
                    setDate(now);
                    setTime(now);
                  }}
                  style={{ lineHeight: 'normal' }}
                  className={`text-lg px-6 py-3 h-14 rounded-full transition-colors font-medium ${date.isSame(moment(), 'day')
                    ? 'bg-journeyAlertsPurple text-white'
                    : 'bg-white text-black border-2 border-journeyAlertsPurple'
                    }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setDate(moment().add(1, 'day'))}
                  style={{ lineHeight: 'normal' }}
                  className={`text-lg px-6 py-3 h-14 rounded-full transition-colors font-medium ${date.isSame(moment().add(1, 'day'), 'day')
                    ? 'bg-journeyAlertsPurple text-white'
                    : 'bg-white text-black border-2 border-journeyAlertsPurple'
                    }`}
                >
                  Tomorrow
                </button>
              </div>
 
              {/* Search Button - In flow for portrait mode */}
              <div className="flex justify-center landscape:hidden">
                <button className={`px-6 sm:px-8 py-3 sm:py-4 rounded-full font-coheadline-bold text-lg sm:text-xl lg:text-2xl transition-colors ${
                  isLoading || !selectedRoute.destination?.name 
                    ? 'bg-gray-300 text-gray-500 opacity-50 cursor-not-allowed' 
                    : 'bg-journeyAlertsYellow text-black'
                }`}
                  onClick={handleSearch}
                  disabled={isLoading || !selectedRoute.destination?.name}>
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Button - Only in landscape mode, positioned at bottom right */}
        <div className="hidden landscape:block absolute bottom-8 right-8">
          <button className={`px-6 sm:px-8 py-3 sm:py-4 rounded-full font-coheadline-bold text-lg sm:text-xl lg:text-2xl transition-colors ${
            isLoading || !selectedRoute.destination?.name 
              ? 'bg-gray-300 text-gray-500 opacity-50 cursor-not-allowed' 
              : 'bg-journeyAlertsYellow text-black hover:bg-yellow-500'
          }`}
            onClick={handleSearch}
            disabled={isLoading || !selectedRoute.destination?.name}>
            Search
          </button>
        </div>
      </div>

      {/* Results Overlay */}
      {nextPlans.length > 0 && selectedPlanIndex === null && (
        <div className="absolute inset-0 z-10 bg-white overflow-y-auto">
          {/* Header with close button */}
          <div className="pt-4 sm:pt-8 px-4 sm:px-8 flex justify-between items-start mb-4 sm:mb-8">
            <div className="flex-1 pr-2">
              <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-black">
                {originDetails.name} to {selectedRoute.destination.name}
              </h2>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600 mt-1">
                {date.format('DD MMMM YYYY')}, after {time.format('HH:mm')}
              </p>
            </div>
            <button
              onClick={() => {
                setNextPlans([]);
                setSelectedPlanIndex(null);
              }}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-black text-white rounded-full flex items-center justify-center transition-colors">
              <span className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-none">×</span>
            </button>
          </div>

          {/* Results List */}
          <div className="px-4 sm:px-8">
            {nextPlans.map((plan, index) => (
              <NextPlan
                key={`plan-${index}-${plan.node?.legs?.[0]?.from?.departure?.scheduledTime || index}`}
                index={index}
                total={nextPlans.length}
                nextPlan={plan}
                isSelected={selectedPlanIndex === index}
                onSelect={() => setSelectedPlanIndex(index)}
                selectedMode={selectedMode}
                selectLocation={{
                  origin: originDetails,
                  destination: selectedRoute.destination
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Journey Summary Overlay */}
      {nextPlans.length > 0 && selectedPlanIndex !== null && !showShareScreen && (
        <div className="absolute inset-0 z-30">
          <JourneySummary
            nextPlan={nextPlans[selectedPlanIndex]}
            onClose={() => setSelectedPlanIndex(null)}
            onSend={() => setShowShareScreen(true)}
            selectedMode={selectedMode}
          />
        </div>
      )}

      {/* Share Screen Overlay */}
      {nextPlans.length > 0 && selectedPlanIndex !== null && showShareScreen && (
        <div className="absolute inset-0 z-40">
          <ShareScreen
            nextPlan={nextPlans[selectedPlanIndex]}
            onClose={() => {
              setShowShareScreen(false);
              setSelectedPlanIndex(null); // Go back to journey plan results instead of summary
            }}
            selectedRoute={selectedRoute}
            selectedMode={selectedMode}
            isStandalone={isStandalone}
            originDetails={originDetails}
          />
        </div>
      )}

      {/* No Results Modal */}
      {showNoResultsModal && (
        <div className="absolute inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-4 p-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <i className="zip-icon-mode_warning text-2xl text-gray-500"></i>
              </div>
              <h2 className="text-2xl font-bold text-black mb-2">No Journeys Found</h2>
              <p className="text-gray-600 mb-6">
                We couldn't find any {selectedMode.toLowerCase()} journeys for your search. 
                Please try a different destination or time.
              </p>
              <button
                onClick={() => setShowNoResultsModal(false)}
                className="w-full bg-black text-white py-3 px-6 rounded-xl font-medium hover:bg-gray-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default JourneyApp;